@import '../../style/index';

.content {
  gap: rem-calc(6px);
}

@mixin button-with-arrow {
  text-align: left;

  @include medium-up {
    min-width: 100%;
  }

  /* stylelint-disable-next-line no-descending-specificity */
  .icon {
    fill: var(--colors-dark-base);
  }

  .tertiary-icon {
    margin-left: rem-calc(6px);
    fill: var(--colors-dark-base);
  }

  .content::after {
    position: absolute;
    bottom: rem-calc(-5px);
    content: '';
    width: 0;
    left: 0;
    border-bottom: 2px solid var(--colors-primary-base);
    transition: width 0.35s;
  }

  &:hover {
    border-color: transparent;

    .content::after {
      width: 100%;
    }
  }

  &:active {
    border-color: transparent;

    .content::after {
      width: 100%;
      border-color: var(--colors-primary-medium);
    }

    .icon {
      fill: var(--colors-dark-light);
    }
  }

  .direction--reverse {
    flex-direction: row-reverse;
  }

  @include add-disabled(var(--colors-grey-base), transparent, transparent);
}

@mixin reset-button-and-anchor {
  button {
    display: block;
    padding: 0;
    border: 0;
    background-color: transparent;
    border-radius: 0;
    text-align: center;
  }

  a {
    text-decoration: none;
    text-align: center;
  }
}

@mixin add-disabled($color, $border, $background) {
  &.button--disabled {
    border-color: $border;
    background: $background;
    color: $color;
    cursor: default;

    .icon {
      fill: $color;
    }

    .tertiary-icon {
      fill: $color;
    }

    .content::after {
      display: none;
    }

    &:active,
    &:hover {
      border-color: $border;
      background: $background;
      color: $color;
      cursor: default;

      .icon {
        fill: $color;
      }
    }
  }
}

@include host {
  --button-line-height: 1;
  --button--underline-icon-height: rem-calc(27px);

  @include reset-button-and-anchor;

  @include medium-up {
    display: inline-block;
  }

  .content {
    display: flex;
    height: inherit;
    position: relative;
    align-items: center;
  }

  .button {
    position: relative;
    width: 100%;
    height: rem-calc(48px);
    display: flex;
    justify-content: center;
    align-items: center;
    padding: rem-calc(12px) rem-calc(16px);
    background: linear-gradient(to right, var(--colors-dark-medium) 50%, var(--colors-dark-base) 50%);
    background-size: 200% 100%;
    background-position: right bottom;
    color: var(--colors-basic-white);
    font-family: $font-family-semibold;
    font-size: rem-calc(16px);
    line-height: var(--button-line-height);
    cursor: pointer;
    user-select: none;
    transition:
      background-position 0.35s cubic-bezier(0.4, 0, 0.3, 1),
      color 0.5s cubic-bezier(0.4, 0, 0.3, 1);

    .direction--reverse {
      flex-direction: row-reverse;
    }

    @include medium-up {
      min-width: rem-calc(96px);
    }

    /* stylelint-disable-next-line no-descending-specificity */
    .icon {
      fill: var(--colors-basic-white);
    }

    &:hover {
      background-position: left bottom;
    }

    &:active {
      background-position: left bottom;
    }

    @include add-disabled(var(--colors-basic-white), var(--colors-grey-base), var(--colors-grey-base));

    &--yellow {
      background: linear-gradient(to right, var(--colors-primary-medium) 50%, var(--colors-primary-base) 50%);
      background-size: 200% 100%;
      background-position: right bottom;
      color: var(--colors-dark-base);

      /* stylelint-disable-next-line no-descending-specificity */
      .icon {
        fill: var(--colors-dark-base);
        transition: fill 0.5s cubic-bezier(0.4, 0, 0.3, 1);
      }

      &:hover {
        background-position: left bottom;
      }

      &:active {
        background-position: left bottom;
      }

      @include add-disabled(var(--colors-basic-white), var(--colors-grey-base), var(--colors-grey-base));
    }

    &--light-grey {
      background: linear-gradient(to right, var(--colors-grey-light) 50%, var(--colors-grey-medium) 50%);
      background-size: 200% 100%;
      background-position: right bottom;
      color: var(--colors-dark-base);

      /* stylelint-disable-next-line no-descending-specificity */
      .icon {
        fill: var(--colors-dark-base);
        transition: fill 0.5s cubic-bezier(0.4, 0, 0.3, 1);
      }

      &:hover {
        background-position: left bottom;
      }

      &:active {
        background-position: left bottom;
      }

      @include add-disabled(var(--colors-basic-white), var(--colors-grey-base), var(--colors-grey-base));
    }

    &--secondary-grey {
      background: transparent;
      color: var(--colors-dark-base);
      border: 2px solid var(--colors-dark-base);

      /* stylelint-disable-next-line no-descending-specificity */
      .icon {
        fill: var(--colors-dark-base);
        transition: fill 0.5s cubic-bezier(0.4, 0, 0.3, 1);
      }

      &:hover {
        border-color: var(--colors-dark-medium);
        color: var(--colors-dark-medium);
        fill: var(--colors-dark-medium);
      }

      &:active {
        border-color: var(--colors-dark-light);
        color: var(--colors-dark-light);
        fill: var(--colors-dark-light);
      }

      @include add-disabled(var(--colors-grey-base), var(--colors-grey-base), transparent);
    }

    &--secondary-white {
      background: transparent;
      color: var(--colors-basic-white);
      border: 2px solid var(--colors-basic-white);

      /* stylelint-disable-next-line no-descending-specificity */
      .icon {
        fill: var(--colors-basic-white);
        transition: fill 0.5s cubic-bezier(0.4, 0, 0.3, 1);
      }

      &:hover {
        border-color: var(--colors-grey-light);
        color: var(--colors-grey-light);

        .icon {
          fill: var(--colors-grey-light);
        }
      }

      &:active {
        border-color: var(--colors-grey-medium);
        color: var(--colors-grey-medium);

        .icon {
          fill: var(--colors-grey-medium);
        }
      }

      @include add-disabled(var(--colors-grey-base), var(--colors-grey-base), transparent);
    }

    &--small {
      font-size: rem-calc(12px);
    }

    &--font--regular {
      font-family: $font-family-regular;
    }

    &--tertiary {
      background: transparent;
      color: var(--colors-dark-base);
      height: rem-calc(27px);
      padding-left: 0;
      padding-right: 0;
      transition: none;
      justify-content: flex-start;
      text-align: left;

      @include button-with-arrow;
    }

    &--modal-close {
      background: transparent;
      color: var(--colors-dark-base);
      height: 2rem;
      width: 2rem;
      padding-left: 0;
      padding-right: 0;
      transition: none;
      justify-content: flex-start;
      text-align: left;
      min-width: auto;

      &:focus {
        outline: 2px solid;
        border-radius: 1px;
      }
    }

    &--global-search {
      background: transparent;
      color: var(--colors-dark-base);
      padding-left: 0;
      padding-right: 0;
      transition: none;
      justify-content: flex-start;
      text-align: left;
      min-width: auto;
    }

    &--default {
      background: transparent;
      color: var(--colors-dark-base);
      padding-left: 0;
      padding-right: 0;
      transition: initial;
      justify-content: initial;
      text-align: left;
      min-width: auto;
    }

    &--clear-input {
      background: transparent;
      color: var(--colors-dark-base);
      padding: 2px 0;
      transition: initial;
      justify-content: center;
      text-align: left;
      min-width: auto;
      height: 100%;
      width: 100%;
      border-radius: 8px;

      &:hover, &:focus {
        background-color: rgba(43, 45, 51, 0.1);
      }

      &:focus {
        outline: 2px solid #2b2d33;
      }
    }

    &--mobile-submenu {
      background: transparent;
      color: var(--colors-dark-base);
      padding: 0;
      margin: 0;
      transition: initial;
      align-items: center;
      text-align: left;
      width: 100%;
      min-width: auto;
      text-transform: uppercase;

      .content {
        justify-content: space-between;
        width: 100%;
      }
    }

    &--underline-icon {
      background: transparent;
      color: var(--colors-dark-base);
      height: var(--button--underline-icon-height);
      padding-left: 0;
      padding-right: 0;
      transition: none;
      justify-content: flex-start;

      @include button-with-arrow;
    }

    &--clear {
      padding-left: 0;
      padding-right: 0;
      min-width: rem-calc(44px);
    }

    &--category {
      height: auto;
      padding: rem-calc(10px) rem-calc(15px);
      background: linear-gradient(to right, var(--colors-grey-light) 50%, transparent 50%);
      background-size: 200% 100%;
      background-position: right bottom;
      color: var(--colors-dark-base);
      border: 1px solid #d6d6d6;
      font-size: rem-calc(12px);

      &:hover {
        background-position: left bottom;
        border: 1px solid #d6d6d6;
      }
    }

    &--navigation-tab {
      background: transparent;
      color: var(--colors-dark-base);
      height: rem-calc(96px);
      width: rem-calc(200px);
      padding: rem-calc(39px) 0 rem-calc(35px) 0;
      transition: none;
      text-transform: uppercase;

      @include medium-up {
        min-width: 100%;
      }

      /* stylelint-disable-next-line no-descending-specificity */
      &::after {
        position: absolute;
        content: '';
        width: 0;
        height: rem-calc(93px);
        left: 0;
        top: 0;
        border-bottom: 2px solid var(--colors-primary-base);
        transition: width 0.35s;
      }

      &:hover,
      &--hovered {
        &::after {
          width: rem-calc(200px);
        }
      }

      &:active {
        &::after {
          width: rem-calc(200px);
          border-color: var(--colors-primary-medium);
        }
      }

      @include add-disabled(var(--colors-grey-base), transparent, transparent);
    }

    &--filter {
      height: initial;
      color: var(--colors-grey-dark);
      background: transparent;
      padding: 0;
      text-decoration: underline;
      font-size: rem-calc(12px);
      line-height: rem-calc(18px);
      display: block;

      &.light {
        font-family: $font-family-regular;
      }
    }

    &--light {
      color: var(--colors-basic-white);

      .icon {
        fill: var(--colors-basic-white);
      }

      .tertiary-icon {
        fill: var(--colors-basic-white);
      }
    }
  }
}
